import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Empty } from "antd";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../design-system";
import { useTailSpend } from "../TailSpendContext";
import { DecisionOption } from "../model/types";
import { DealHeaderCard } from "../components/DealHeaderCard";
import { NegotiationStream } from "../components/NegotiationStream";
import { DealSidePanels } from "../components/DealSidePanels";
import classes from "./DealDetailView.module.less";

const DEFAULT_DETAILS_WIDTH = 600;
const MIN_DETAILS_WIDTH = 360;
const MIN_STREAM_WIDTH = 480;

export const DealDetailView: React.FC = () => {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const { workspace, dispatch } = useTailSpend();
    const [detailsWidth, setDetailsWidth] = useState(DEFAULT_DETAILS_WIDTH);
    const streamRef = useRef<HTMLDivElement>(null);
    const workAreaRef = useRef<HTMLDivElement>(null);

    const deal = workspace.deals.find((d) => d.id === dealId);

    useLayoutEffect(() => {
        const el = streamRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [dealId]);

    useEffect(() => {
        if (!deal) return;
        const el = streamRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [deal?.escalation?.resolution, deal?.auditTrail.length, deal]);

    const startResize = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = detailsWidth;
            const workArea = workAreaRef.current;
            if (!workArea) return;
            const workAreaWidth = workArea.getBoundingClientRect().width;
            const maxDetailsWidth = Math.max(
                MIN_DETAILS_WIDTH,
                workAreaWidth - MIN_STREAM_WIDTH
            );

            const onMove = (ev: PointerEvent) => {
                const delta = startX - ev.clientX;
                const next = Math.min(
                    maxDetailsWidth,
                    Math.max(MIN_DETAILS_WIDTH, startWidth + delta)
                );
                setDetailsWidth(next);
            };
            const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        },
        [detailsWidth]
    );

    if (!deal) {
        return (
            <>
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() =>
                        navigate("/superadmin/demo/tail-spend/negotiations")
                    }
                >
                    Back to negotiations
                </Button>
                <Empty description="Deal not found." style={{ padding: 80 }} />
            </>
        );
    }

    const handleDecide = (option: DecisionOption) => {
        dispatch({
            type: "HumanDecision",
            dealId: deal.id,
            option: option.kind,
            optionTitle: option.title,
            byName: workspace.currentUser.name,
        });
    };

    const handleSendMessage = (text: string) => {
        dispatch({
            type: "SendDirectMessage",
            dealId: deal.id,
            byName: workspace.currentUser.name,
            preview: text,
        });
    };

    return (
        <div className={classes.layout}>
            <DealHeaderCard
                deal={deal}
                actions={
                    <Button
                        type="text"
                        shape="circle"
                        icon={<CloseOutlined />}
                        onClick={() => navigate(-1)}
                        aria-label="Close"
                    />
                }
            />
            <div
                ref={workAreaRef}
                className={classes.workArea}
                style={{
                    gridTemplateColumns: `minmax(0, 1fr) 12px ${detailsWidth}px`,
                }}
            >
                <div className={classes.streamArea}>
                    <div className={classes.streamWrap} ref={streamRef}>
                        <div className={classes.streamInner}>
                            <NegotiationStream
                                deal={deal}
                                onDecide={handleDecide}
                                onSendMessage={handleSendMessage}
                            />
                        </div>
                    </div>
                    <div className={classes.streamFade} />
                </div>
                <div
                    className={classes.resizer}
                    onPointerDown={startResize}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize details panel"
                >
                    <div className={classes.resizerGrip} />
                </div>
                <aside className={classes.detailsPanel}>
                    <div className={classes.detailsScroll}>
                        <DealSidePanels deal={deal} />
                    </div>
                </aside>
            </div>
        </div>
    );
};
